import React, { useState } from 'react'
import { Nav, Tab, Badge } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { cn, day } from 'utils'

function LicenseSearch({ value, onChange, t }) {
	return (
		<div className="mb-3">
			<input
				type="text"
				className="form-control"
				placeholder={t('license_manager.list.search_placeholder')}
				value={value}
				onChange={e => onChange(e.target.value)}
			/>
		</div>
	)
}

/**
 * Tab 1: Alle Lizenzen
 * Self-purchased licenses with full checkout details on top,
 * followed by organization licenses (purchased by others) without checkout details.
 */
function AllLicensesTable({ ownLicenses, orgLicenses, t }) {
	return (
		<>
			{/* Self-purchased licenses with checkout details */}
			{ownLicenses.length > 0 && (
				<>
					<h5 className="mt-2 mb-3">{t('license_manager.list.section_own')}</h5>
					<table className="table">
						<thead>
							<tr>
								<th>{t('license_manager.list.publication')}</th>
								<th>{t('license_manager.list.source')}</th>
								<th>{t('license_manager.list.date')}</th>
								<th>{t('license_manager.list.activated')}</th>
							</tr>
						</thead>
						<tbody>
							{ownLicenses.map((license) => {
								const { product, available, quantity, order, subscription, validFrom, checkouts } = license
								if (!product) return null
								const sourceNumber = (order && order.number) || (subscription && subscription.number)
								const hasCheckouts = checkouts && checkouts.length > 0
								const rowCount = hasCheckouts ? checkouts.length + 1 : 1

								return (
									<React.Fragment key={license.id}>
										<tr>
											<td rowSpan={rowCount}>
												<strong>{product.name}</strong>
												<br />
												<small className="text-muted">{product.title}</small>
											</td>
											<td rowSpan={rowCount}>
												{order && <span>{t('license_manager.list.order')} {sourceNumber}</span>}
												{subscription && <span>{t('license_manager.list.subscription')} {sourceNumber}</span>}
											</td>
											<td rowSpan={rowCount}>
												{day(validFrom).format('DD.MM.YYYY')}
											</td>
											<td>
												<span className={cn({ 'text-success': available > 0, 'text-danger': available === 0 })}>
													{quantity - available} / {quantity}
												</span>
											</td>
										</tr>
										{hasCheckouts && checkouts.map((checkout) => {
											const isShared = !!checkout.emailedTo
											const isClaimed = !!checkout.user
											return (
												<tr key={checkout.id} className="border-top-0">
													<td className="py-1">
														<div className="d-flex align-items-center">
															{isShared
																? <Badge variant="info" className="mr-2">{t('license_manager.list.badge_shared')}</Badge>
																: <Badge variant="primary" className="mr-2">{t('license_manager.list.badge_internal')}</Badge>
															}
															{isClaimed
																? <span>{checkout.user.userFirstname} {checkout.user.userLastname} <small className="text-muted">({checkout.user.email})</small></span>
																: isShared
																	? <span className="text-muted">
																		{checkout.emailedTo}
																		{checkout.emailedAt && <small className="ml-1">– {t('license_manager.list.sent_at')} {day(checkout.emailedAt).format('DD.MM.YYYY')}</small>}
																	</span>
																	: <span className="text-muted">–</span>
															}
														</div>
													</td>
												</tr>
											)
										})}
									</React.Fragment>
								)
							})}
						</tbody>
					</table>
				</>
			)}

			{/* Organization licenses without checkout details */}
			{orgLicenses.length > 0 && (
				<>
					<h5 className="mt-4 mb-3">{t('license_manager.list.section_org')}</h5>
					<table className="table">
						<thead>
							<tr>
								<th>{t('license_manager.list.publication')}</th>
								<th>{t('license_manager.list.source')}</th>
								<th>{t('license_manager.list.date')}</th>
								<th>{t('license_manager.list.activated')}</th>
							</tr>
						</thead>
						<tbody>
							{orgLicenses.map((license) => {
								const { product, available, quantity, order, subscription, validFrom } = license
								if (!product) return null
								const purchaserUser = order?.user || subscription?.user
								const sourceNumber = (order && order.number) || (subscription && subscription.number)
								return (
									<tr key={license.id}>
										<td>
											<strong>{product.name}</strong>
											<br />
											<small className="text-muted">{product.title}</small>
										</td>
										<td>
											<div>
												{order && <span>{t('license_manager.list.order')} {sourceNumber}</span>}
												{subscription && <span>{t('license_manager.list.subscription')} {sourceNumber}</span>}
											</div>
											{purchaserUser && (
												<small className="text-muted">
													{t('license_manager.list.purchased_org', { name: `${purchaserUser.userFirstname} ${purchaserUser.userLastname}` })}
												</small>
											)}
										</td>
										<td>{day(validFrom).format('DD.MM.YYYY')}</td>
										<td>
											<span className={cn({ 'text-success': available > 0, 'text-danger': available === 0 })}>
												{quantity - available} / {quantity}
											</span>
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</>
			)}
		</>
	)
}

/**
 * Tab 2: Meine Lizenzen
 * Checkouts that the current user has personally activated.
 */
function MyLicensesTable({ checkouts, t }) {
	return (
		<table className="table">
			<thead>
				<tr>
					<th>{t('license_manager.list.publication')}</th>
					<th>{t('license_manager.list.activated_at')}</th>
					<th>{t('license_manager.list.info')}</th>
					<th></th>
				</tr>
			</thead>
			<tbody>
				{checkouts.map((checkout) => {
					const product = checkout.product
					if (!product) return null
					return (
						<tr key={checkout.id}>
							<td>
								<strong>{product.name}</strong>
								<br />
								<small className="text-muted">{product.title}</small>
							</td>
							<td>
								{checkout.activatedAt
									? day(checkout.activatedAt).format('DD.MM.YYYY')
									: '–'
								}
							</td>
							<td>
								{checkout.checkoutComment && (
									<div><small>{checkout.checkoutComment}</small></div>
								)}
								{checkout.checkoutToken && (
									<div><small className="text-muted">{t('license_manager.list.checkout_token')}: {checkout.checkoutToken}</small></div>
								)}
							</td>
							<td className="text-right">
								{checkout.actionUrl && (
									<a
										href={checkout.actionUrl}
										target="_blank"
										rel="noopener noreferrer"
										className="btn btn-sm btn-outline-primary"
									>
										{t('license_manager.list.start_service')}
									</a>
								)}
							</td>
						</tr>
					)
				})}
			</tbody>
		</table>
	)
}

/**
 * Tab 3: Geteilte Lizenzen
 * Checkouts that were shared via email, with status (invited / active).
 */
function SharedLicensesTable({ licenses, t }) {
	return (
		<table className="table">
			<thead>
				<tr>
					<th>{t('license_manager.list.publication')}</th>
					<th>{t('license_manager.list.email')}</th>
					<th>{t('license_manager.list.shared_at')}</th>
					<th>{t('license_manager.list.info')}</th>
					<th>{t('license_manager.list.status')}</th>
					<th></th>
				</tr>
			</thead>
			<tbody>
				{licenses.map((license) => {
					const { product, checkouts } = license
					if (!product) return null
					return (checkouts || []).map((checkout) => {
						const isActive = !!checkout.user && !!checkout.activatedAt
						return (
							<tr key={checkout.id}>
								<td>
									<strong>{product.name}</strong>
									<br />
									<small className="text-muted">{product.title}</small>
								</td>
								<td>
									{checkout.emailedTo || '–'}
									{isActive && checkout.user && (
										<div><small className="text-muted">{checkout.user.userFirstname} {checkout.user.userLastname}</small></div>
									)}
								</td>
								<td>
									{checkout.emailedAt
										? day(checkout.emailedAt).format('DD.MM.YYYY')
										: '–'
									}
								</td>
								<td>
									{checkout.checkoutComment && (
										<div><small>{checkout.checkoutComment}</small></div>
									)}
									{checkout.checkoutToken && (
										<div><small className="text-muted">{t('license_manager.list.checkout_token')}: {checkout.checkoutToken}</small></div>
									)}
								</td>
								<td>
									{isActive
										? <Badge variant="success">{t('license_manager.list.active')}</Badge>
										: <Badge variant="warning">{t('license_manager.list.invited')}</Badge>
									}
								</td>
								<td className="text-right">
									{checkout.actionUrl && (
										<a
											href={checkout.actionUrl}
											target="_blank"
											rel="noopener noreferrer"
											className="btn btn-sm btn-outline-primary"
										>
											{t('license_manager.list.start_service')}
										</a>
									)}
								</td>
							</tr>
						)
					})
				})}
			</tbody>
		</table>
	)
}

export default function LicenseManager({ licenses, checkouts, currentUserId }) {
	const { t } = useTranslation('account')
	const [activeTab, setActiveTab] = useState('all')
	const [searchAll, setSearchAll] = useState('')
	const [searchMy, setSearchMy] = useState('')
	const [searchShared, setSearchShared] = useState('')

	// Tab 1: Split licenses by self-purchased vs organization
	const ownLicenses = licenses.filter(({ isOwner, order, subscription }) => {
		if (!isOwner) return false
		const purchaser = order?.user || subscription?.user
		return purchaser && purchaser.id === currentUserId
	})
	const orgLicenses = licenses.filter(({ isOwner, order, subscription }) => {
		if (!isOwner) return false
		const purchaser = order?.user || subscription?.user
		return !purchaser || purchaser.id !== currentUserId
	})
	const allLicenses = [...ownLicenses, ...orgLicenses]

	// Tab 2: Checkouts that belong to the current user (user-level checkouts from the query)
	const myCheckouts = checkouts.filter(c => !!c.activatedAt)

	// Tab 3: Shared checkouts (licenses with emailedTo set on any checkout)
	const sharedLicenses = licenses
		.filter(({ isOwner }) => isOwner)
		.map(license => ({
			...license,
			checkouts: (license.checkouts || []).filter(c => !!c.emailedTo)
		}))
		.filter(license => license.checkouts.length > 0)

	const filterLicenses = (items, keyword) => {
		if (!keyword.trim()) return items
		const lower = keyword.toLowerCase()
		return items.filter(({ product }) =>
			product && (
				product.name.toLowerCase().includes(lower) ||
				product.title.toLowerCase().includes(lower)
			)
		)
	}

	const filterCheckouts = (items, keyword) => {
		if (!keyword.trim()) return items
		const lower = keyword.toLowerCase()
		return items.filter(({ product }) =>
			product && (
				product.name.toLowerCase().includes(lower) ||
				product.title.toLowerCase().includes(lower)
			)
		)
	}

	const filteredAll = filterLicenses(allLicenses, searchAll)
	const filteredMy = filterCheckouts(myCheckouts, searchMy)
	const filteredShared = filterLicenses(sharedLicenses, searchShared)

	return (
		<div className="license-manager mt-4">
			<h2>{t('license_manager.manage.title')}</h2>
			<Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
				<Nav fill variant="tabs" className="mb-3">
					<Nav.Item>
						<Nav.Link eventKey="all">
							{t('license_manager.manage.all_licenses')}
						</Nav.Link>
					</Nav.Item>
					<Nav.Item>
						<Nav.Link eventKey="my">
							{t('license_manager.manage.my_licenses')}
						</Nav.Link>
					</Nav.Item>
					<Nav.Item>
						<Nav.Link eventKey="shared">
							{t('license_manager.manage.shared_licenses')}
						</Nav.Link>
					</Nav.Item>
				</Nav>
				<Tab.Content>
					<Tab.Pane eventKey="my">
						<LicenseSearch value={searchMy} onChange={setSearchMy} t={t} />
						{filteredMy.length === 0 && (
							<p className="text-muted">{t('license_manager.manage.empty_my')}</p>
						)}
						{filteredMy.length > 0 && (
							<MyLicensesTable checkouts={filteredMy} t={t} />
						)}
					</Tab.Pane>
					<Tab.Pane eventKey="shared">
						<LicenseSearch value={searchShared} onChange={setSearchShared} t={t} />
						{filteredShared.length === 0 && (
							<p className="text-muted">{t('license_manager.manage.empty_shared')}</p>
						)}
						{filteredShared.length > 0 && (
							<SharedLicensesTable
								licenses={filteredShared}
								t={t}
							/>
						)}
					</Tab.Pane>
					<Tab.Pane eventKey="all">
						<LicenseSearch value={searchAll} onChange={setSearchAll} t={t} />
						{filteredAll.length === 0 && (
							<p className="text-muted">{t('license_manager.manage.empty_all')}</p>
						)}
						{filteredAll.length > 0 && (
							<AllLicensesTable
								ownLicenses={filterLicenses(ownLicenses, searchAll)}
								orgLicenses={filterLicenses(orgLicenses, searchAll)}
								t={t}
							/>
						)}
					</Tab.Pane>
				</Tab.Content>
			</Tab.Container>
		</div>
	)
}
